// Design descriptors — what TYPE each globalVariables path is.
//
// `globalVariables` is an untyped bag. A real snapshot holds `"#FCF9E8"` next to
// `{portrait: 0.1, landscape: 0.12}` next to `{x: 0.5, y: 0.945}` next to a live
// scoreboard, and the raw Custom Variables tab can only tell them apart by
// `typeof` — so a colour renders as a text box and a runtime counter renders as
// a setting. A design says which is which, and the Design tab renders it.
//
// PURE. No React, nothing fetched, nothing persisted. The panel owns the I/O;
// this owns the meaning, which is what makes both testable.
//
// A DESIGN IS PRESENTATION ONLY. It never changes a value and never reshapes a
// snapshot. Everything it describes still lives in `globalVariables`, and the
// raw tab keeps showing all of it — a design narrows the view, it does not own
// the data.

import _ from "lodash";

/** The field types a design may declare. Anything else is dropped on load. */
export const DESIGN_FIELD_TYPES = new Set([
  "boolean",
  "number",
  // A `number` that must stay whole: the row rounds what it writes and steps by
  // one. Declared by hand, never guessed — a value that reads `1` today may well
  // want `1.06` tomorrow, and rounding it away would be a silent loss.
  "integer",
  "string",
  "color",
  "vec2",
  // Both are edited as a JSON document — in the row, or in the full-size editor
  // the row offers. Neither says anything about how the value is STORED: a
  // level table kept as a string stays a string.
  "array",
  "object",
  // Not a `globalVariables` value at all — see ASSET_FIELD_PREFIX below.
  "asset",
]);

/**
 * Types a design file may still carry, and what they are now.
 *
 * `json` was briefly a type of its own before `object` was understood to cover
 * it, and `float` before `number` was understood to be the decimal one. A design
 * written then keeps working rather than losing the row.
 */
const LEGACY_TYPE_ALIASES = { json: "object", float: "number" };

/**
 * What marks a field as naming an ASSET rather than a variable.
 *
 * An asset row does not describe a value in `globalVariables`; it describes one
 * entry in the snapshot's asset map, so that a background or a font can sit in
 * the same group as the colours it belongs with and be replaced there.
 *
 * The prefix carries that difference in the field's `path` because **path is
 * identity** here: `designFieldsByPath`, `designHasFieldPath`, `moveField`,
 * `removeField` and `addField` all key on it. A dotted variable path can never
 * contain a colon, so `asset:bgImg` cannot collide with a variable however the
 * snapshot is shaped — and every one of those helpers keeps working untouched,
 * which is what makes an asset row group, drag and delete like any other row.
 */
export const ASSET_FIELD_PREFIX = "asset:";

/** The asset key a field path names, or null when it is not an asset path. */
export function assetKeyFromPath(path) {
  const text = typeof path === "string" ? path.trim() : "";
  if (!text.startsWith(ASSET_FIELD_PREFIX)) return null;
  const key = text.slice(ASSET_FIELD_PREFIX.length).trim();
  return key || null;
}

/** The field path for an asset key — accepts a key that is already prefixed. */
export function assetFieldPath(assetKey) {
  const key = String(assetKey ?? "").trim();
  if (!key) return "";
  return key.startsWith(ASSET_FIELD_PREFIX) ? key : `${ASSET_FIELD_PREFIX}${key}`;
}

/** Numbers can render as the scrub field (default) or as a slider. */
export const NUMBER_CONTROLS = new Set(["scrub", "slider"]);

export const DESIGN_FORMAT_VERSION = 1;

/**
 * A scaffold stops here. Deep enough for `tuning.hint.hintCountPosition`, which
 * is the real shape these snapshots reach; past that a generated design is
 * noise nobody will read, and the author can add the path by hand.
 */
export const SCAFFOLD_MAX_DEPTH = 4;

/**
 * Past this many fields a generated group is a wall, so it splits into its
 * sub-containers instead.
 *
 * Measured rather than guessed: one group per top-level key suits wordmaster,
 * whose largest container holds 35 fields, but ruins puzzle.json — everything
 * there hangs off `cfg` and `tuning`, giving groups of 208 and 129. Splitting at
 * 40 leaves wordmaster untouched and turns puzzle into readable sections.
 */
export const SCAFFOLD_MAX_FIELDS_PER_GROUP = 40;

// ---------------------------------------------------------------------------
// Value sniffing
//
// Moved here from containerPlayEngineVersionSettings, which had them nested
// inside the component by accident of file organisation — they close over
// nothing. The scaffold generator and the component inspector have to agree
// about what a value IS, and one copy is how that stays true.
// ---------------------------------------------------------------------------

/** `{portrait, landscape}` — one value per orientation. */
export function isOrientationMap(value) {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    ("portrait" in value || "landscape" in value)
  );
}

/** A plain object that is not itself a recognised compound value. */
export function isPlainConfigObject(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  if ("portrait" in value || "landscape" in value) return false;
  if ("x" in value && "y" in value && Object.keys(value).length === 2) return false;
  return true;
}

/**
 * A path whose LAST segment names a colour — `color`, `fontColor`, `bgColour`.
 *
 * The value alone is not enough: a colour that has not been set yet is `""`,
 * and half the snapshots in a flow ship with one. Reading the name keeps those
 * rows on the swatch instead of dropping them to a text box, which is where
 * someone then types a word instead of a hex value.
 */
function isColorPath(dottedPath) {
  const last = String(dottedPath || "").split(".").pop();
  return /colou?r$/i.test(last);
}

/**
 * What a value looks like, with no schema to go on.
 *
 * Returns the engine's own vocabulary (`ConfigFieldSchema` in DashboardBridge),
 * including its `responsive-*` spellings, so a sniffed type and an engine-
 * supplied one are the same strings. `normalizeField` folds the `responsive-`
 * prefix into a modifier afterwards.
 *
 * @param {*} value
 * @param {string} [dottedPath] the value's path, for the rules the value alone
 *   cannot decide — see isColorPath.
 * @returns {string|null} null for a value no editor can represent.
 */
export function detectFieldType(value, dottedPath = "") {
  // Orientation first: the inner value decides the control, the outer shape
  // only decides that there are two of them.
  if (isOrientationMap(value)) {
    const inner = value.portrait ?? value.landscape;
    if (typeof inner === "number") return "responsive-number";
    if (typeof inner === "boolean") return "responsive-boolean";
    if (typeof inner === "string") return "responsive-string";
    if (typeof inner === "object" && inner !== null && "x" in inner && "y" in inner) {
      return "responsive-vec2";
    }
  }
  if (typeof value === "boolean") return "boolean";
  // Always `number`, never `integer`: a value that happens to be whole right now
  // is not a value that must STAY whole, and the wrong guess rounds the next
  // edit away. `integer` is the author's call — see LEGACY_TYPE_ALIASES for the
  // one spelling that is folded away.
  if (typeof value === "number") return "number";
  if (typeof value === "string" && /^#[0-9a-f]{3,8}$/i.test(value)) return "color";
  // An empty or half-typed colour is still a colour. The NAME is the better
  // evidence than the value here: `background.color` starts life as "" often
  // enough that reading it as a text box is the common case, not the rare one.
  if (typeof value === "string" && isColorPath(dottedPath)) return "color";
  if (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    "y" in value &&
    Object.keys(value).length === 2
  ) {
    return "vec2";
  }
  if (typeof value === "string") return "string";
  if (Array.isArray(value)) return "array";
  if (isPlainConfigObject(value)) return "object";
  return null;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/**
 * `textFontColor` → `Text Font Color`, `hint_count` → `Hint Count`.
 *
 * Every word is capitalised, not just the first: a camelCase key already
 * arrives as several words, so capitalising only the first would spell the two
 * naming styles differently in the same panel.
 */
export function humanizeKey(key) {
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/(^|\s)\S/g, (character) => character.toUpperCase());
}

/**
 * A label for a path relative to its group.
 *
 * `tuning.hint.position` inside the `tuning` group reads `Hint › Position`: the
 * intermediate segments are what tell two `position` fields apart, and dropping
 * them is how a generated design becomes a list of identical rows.
 */
export function labelForPath(dottedPath, groupPath) {
  const parts = String(dottedPath).split(".");
  const groupDepth = groupPath ? String(groupPath).split(".").length : 0;
  const tail = parts.slice(groupDepth);
  return (tail.length ? tail : parts).map(humanizeKey).join(" › ");
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * One field, in the shape the renderer wants.
 *
 * `responsive-number` and `{type: "number", responsive: true}` mean the same
 * thing and both are accepted — the first is what the sniffer produces and what
 * the engine calls it, the second is what reads better in a hand-written file.
 * They are collapsed to the second here so the renderer only ever sees one.
 *
 * @returns {object|null} null for a field no renderer could use.
 */
function normalizeField(raw, groupPath) {
  if (!_.isPlainObject(raw)) return null;

  const path = typeof raw.path === "string" ? raw.path.trim() : "";
  if (!path) return null;

  let type = typeof raw.type === "string" ? raw.type.trim() : "";
  let responsive = raw.responsive === true;
  if (type.startsWith("responsive-")) {
    type = type.slice("responsive-".length);
    responsive = true;
  }
  type = LEGACY_TYPE_ALIASES[type] || type;
  if (!DESIGN_FIELD_TYPES.has(type)) return null;

  // An asset row carries none of the value modifiers: there is no orientation
  // pair, no slider bounds, no enum and no translation for a picture. Dropping
  // them here means the renderer never has to ask.
  if (type === "asset") {
    // The prefix is required, not merely accepted: the tree helpers edit the RAW
    // design, so a row whose normalized path differed from the one written in
    // the file could be shown but never moved or removed. `validateDesign` is
    // what tells the author about a path that was dropped here.
    const assetKey = assetKeyFromPath(path);
    if (!assetKey) return null;
    return {
      path: assetFieldPath(assetKey),
      assetKey,
      type,
      responsive: false,
      label:
        typeof raw.label === "string" && raw.label.trim() ? raw.label.trim() : humanizeKey(assetKey),
      help: typeof raw.help === "string" ? raw.help : null,
      control: "scrub",
      min: null,
      max: null,
      step: null,
      options: null,
      localizable: false,
      readOnly: raw.readOnly === true,
    };
  }

  const options = Array.isArray(raw.options)
    ? raw.options
      .map((option) =>
        _.isPlainObject(option)
          ? { value: option.value, label: String(option.label ?? option.value) }
          : { value: option, label: String(option) }
      )
      .filter((option) => option.value !== undefined)
    : null;

  return {
    path,
    type,
    responsive,
    label: typeof raw.label === "string" && raw.label.trim() ? raw.label.trim() : labelForPath(path, groupPath),
    help: typeof raw.help === "string" ? raw.help : null,
    // A slider needs bounds to be a slider at all; without them it silently
    // stays the scrub field rather than rendering a thumb with nowhere to go.
    control:
      raw.control === "slider" && _.isFinite(raw.min) && _.isFinite(raw.max) ? "slider" : "scrub",
    min: _.isFinite(raw.min) ? raw.min : null,
    max: _.isFinite(raw.max) ? raw.max : null,
    step: _.isFinite(raw.step) ? raw.step : null,
    options: options && options.length ? options : null,
    localizable: raw.localizable === true,
    readOnly: raw.readOnly === true,
  };
}

/**
 * Turn a design as written on disk into the shape the renderer wants.
 *
 * FORGIVING BY DESIGN. A field it cannot use is dropped, not thrown on: these
 * files are hand-edited, and one bad row must not blank the whole tab. Use
 * `validateDesign` to tell the author what was dropped and why.
 */
/**
 * EMPTY GROUPS ARE KEPT. An earlier cut dropped them as "a heading over empty
 * space", which is right for a flat list and wrong for a tree: a group someone
 * just made has nothing in it yet, and it has to be visible to be dragged into.
 * A group whose fields were all rejected shows up empty too — the problems
 * banner is what explains that, not its disappearance.
 */
function normalizeGroups(rawGroups, parentId) {
  return (Array.isArray(rawGroups) ? rawGroups : [])
    .filter(_.isPlainObject)
    .map((group, index) => {
      const id =
        typeof group.id === "string" && group.id.trim()
          ? group.id.trim()
          : `${parentId ? `${parentId}/` : ""}group${index}`;
      const groupPath = typeof group.path === "string" ? group.path.trim() : "";
      const fields = (Array.isArray(group.fields) ? group.fields : [])
        .map((field) => normalizeField(field, groupPath))
        .filter(Boolean);

      return {
        id,
        label:
          typeof group.label === "string" && group.label.trim()
            ? group.label.trim()
            : humanizeKey(groupPath || id),
        path: groupPath || null,
        fields,
        groups: normalizeGroups(group.groups, id),
      };
    });
}

export function normalizeDesign(raw) {
  if (!_.isPlainObject(raw)) return null;

  const groups = normalizeGroups(raw.groups, null);

  return {
    formatVersion: _.isFinite(raw.formatVersion) ? raw.formatVersion : DESIGN_FORMAT_VERSION,
    id: typeof raw.id === "string" ? raw.id : null,
    title: typeof raw.title === "string" && raw.title.trim() ? raw.title.trim() : null,
    target: typeof raw.target === "string" ? raw.target : "globalVariables",
    groups,
  };
}

/** Every group in a normalized design, parents before their children. */
export function walkGroups(design) {
  const out = [];
  function visit(groups, depth, ancestors) {
    for (const group of groups || []) {
      out.push({ group, depth, ancestors });
      visit(group.groups, depth + 1, [...ancestors, group.id]);
    }
  }
  visit(_.get(design, "groups", []), 0, []);
  return out;
}

/** Every field of a normalized design, keyed by the path it writes to. */
export function designFieldsByPath(design) {
  const byPath = new Map();
  for (const { group } of walkGroups(design)) {
    for (const field of group.fields) {
      if (!byPath.has(field.path)) byPath.set(field.path, field);
    }
  }
  return byPath;
}

/**
 * What is wrong with a design, in the author's terms.
 *
 * Separate from `normalizeDesign` on purpose: loading has to succeed with the
 * good rows, and the author still has to be told about the bad ones. Returns an
 * empty array for a design with nothing wrong.
 */
export function validateDesign(raw) {
  const problems = [];
  if (!_.isPlainObject(raw)) return [{ where: "design", message: "Design must be a JSON object." }];
  if (!Array.isArray(raw.groups)) {
    return [{ where: "design", message: "Design must have a `groups` array." }];
  }

  const seen = new Map();

  // Nested groups are checked with the same rules at every depth, and `where`
  // spells out the whole route so a problem in a child is findable in the file.
  function checkGroups(groups, prefix) {
    groups.forEach((group, groupIndex) => {
      const where = `${prefix}[${groupIndex}]`;
      if (!_.isPlainObject(group)) {
        problems.push({ where, message: "Group must be an object." });
        return;
      }
      if (group.groups !== undefined && !Array.isArray(group.groups)) {
        problems.push({ where, message: "`groups` must be an array when present." });
      }
      // A folder holding only other folders has no fields of its own, which is
      // a legitimate shape rather than a mistake.
      const hasChildren = Array.isArray(group.groups) && group.groups.length > 0;
      if (!Array.isArray(group.fields)) {
        if (!hasChildren) {
          problems.push({ where, message: "Group must have a `fields` array." });
          return;
        }
      } else {
        checkFields(group, where);
      }
      if (Array.isArray(group.groups)) checkGroups(group.groups, `${where}.groups`);
    });
  }

  function checkFields(group, where) {
    group.fields.forEach((field, fieldIndex) => {
      const fieldWhere = `${where}.fields[${fieldIndex}]`;
      if (!_.isPlainObject(field)) {
        problems.push({ where: fieldWhere, message: "Field must be an object." });
        return;
      }
      if (typeof field.path !== "string" || !field.path.trim()) {
        problems.push({ where: fieldWhere, message: "Field needs a `path`." });
        return;
      }

      const bareType = String(field.type || "").replace(/^responsive-/, "");
      if (!DESIGN_FIELD_TYPES.has(bareType)) {
        problems.push({
          where: fieldWhere,
          message: `Unknown type "${field.type}". Expected one of: ${[...DESIGN_FIELD_TYPES].join(", ")}.`,
        });
      }
      if (bareType === "asset") {
        if (!assetKeyFromPath(field.path)) {
          problems.push({
            where: fieldWhere,
            message: `An asset field's path must be "${ASSET_FIELD_PREFIX}<asset key>" — got "${field.path}".`,
          });
        }
        // Said rather than silently dropped: these read as promises in the file.
        const ignored = ["responsive", "options", "min", "max", "step", "control", "localizable"]
          .filter((key) => field[key] !== undefined);
        if (ignored.length) {
          problems.push({
            where: fieldWhere,
            message: `Ignored on an asset row: ${ignored.join(", ")}.`,
          });
        }
      }
      if (field.control === "slider" && !(_.isFinite(field.min) && _.isFinite(field.max))) {
        problems.push({
          where: fieldWhere,
          message: "A slider needs both `min` and `max`; falling back to the number field.",
        });
      }

      // Two fields on one path means two controls writing the same value —
      // whichever renders second silently wins.
      const previous = seen.get(field.path);
      if (previous) {
        problems.push({
          where: fieldWhere,
          message: `Duplicate path "${field.path}", already declared at ${previous}.`,
        });
      } else {
        seen.set(field.path, fieldWhere);
      }
    });
  }

  checkGroups(raw.groups, "groups");

  return problems;
}

/**
 * Which control a field gets.
 *
 * Kept here rather than inside the renderer's switch because it is a decision,
 * not markup: `options` outranks the declared type, and a `number` splits into
 * two different controls depending on whether the design gave it bounds. Both
 * are the kind of rule worth pinning in a test.
 *
 * @returns {"dropdown"|"boolean"|"color"|"string"|"vec2"|"slider"|"number"|"integer"|"json"}
 */
export function designControlKind(field) {
  if (!field) return "json";
  // Before the options check: an asset is a slot, not a value, so a stray
  // `options` list on one must not turn it into a dropdown of strings.
  if (field.type === "asset") return "asset";
  // A value with a list of allowed values is a dropdown whatever else it is.
  if (field.options && field.options.length) return "dropdown";

  switch (field.type) {
  case "boolean":
    return "boolean";
  case "color":
    return "color";
  case "string":
    return "string";
  case "vec2":
    return "vec2";
  case "number":
    return field.control === "slider" ? "slider" : "number";
  case "integer":
    return field.control === "slider" ? "slider" : "integer";
  default:
    // array, object, and anything a future design names that this build does
    // not know: JSON keeps the value reachable instead of hiding it.
    return "json";
  }
}

// ---------------------------------------------------------------------------
// Editing the tree
//
// These work on the RAW design — the object that gets written back to disk —
// not on the normalized one, so what is saved stays the file someone can open
// and read. Every one returns a new design; none mutates its argument.
//
// The design file IS the order. There is no side-car order map like the scene
// editor's `categoryData`, because that exists to impose an order on components
// whose own storage has none. Here the array is the order, so reordering is
// moving an element.
// ---------------------------------------------------------------------------

/**
 * Give every group a stable id, so a drag has something to name.
 *
 * A hand-written design need not carry ids; normalizeDesign invents them from
 * position, which changes the moment anything moves. This pins them once, on
 * load, and the ids match what normalizeDesign would have produced so the
 * rendered tree and the raw tree agree.
 */
export function ensureGroupIds(raw) {
  if (!_.isPlainObject(raw)) return raw;

  function walk(groups, parentId) {
    return (Array.isArray(groups) ? groups : []).map((group, index) => {
      if (!_.isPlainObject(group)) return group;
      const id =
        typeof group.id === "string" && group.id.trim()
          ? group.id.trim()
          : `${parentId ? `${parentId}/` : ""}group${index}`;
      const next = { ...group, id };
      if (Array.isArray(group.groups)) next.groups = walk(group.groups, id);
      return next;
    });
  }

  return { ...raw, groups: walk(raw.groups, null) };
}

/** The group with this id, its parent list, and its index — or null. */
function locateGroup(groups, id, parent = null) {
  const list = Array.isArray(groups) ? groups : [];
  for (let index = 0; index < list.length; index++) {
    const group = list[index];
    if (!_.isPlainObject(group)) continue;
    if (group.id === id) return { group, siblings: list, index, parent };
    const found = locateGroup(group.groups, id, group);
    if (found) return found;
  }
  return null;
}

/** Whether `ancestorId` is `id` or contains it. */
export function isGroupAncestor(raw, ancestorId, id) {
  const located = locateGroup(_.get(raw, "groups", []), ancestorId);
  if (!located) return false;
  if (ancestorId === id) return true;
  return Boolean(locateGroup(located.group.groups, id));
}

/**
 * Move a group somewhere else in the tree.
 *
 * @param {object} raw
 * @param {string} draggedId
 * @param {string} targetId
 * @param {"before"|"after"|"inside"} position
 * @returns {object} a new design, or the original when the move is impossible
 */
export function moveGroup(raw, draggedId, targetId, position) {
  if (!_.isPlainObject(raw) || draggedId === targetId) return raw;

  const source = locateGroup(raw.groups, draggedId);
  if (!source) return raw;

  // Dropping a group inside itself would take the whole subtree out of the
  // document — the classic way to lose half a design to one careless drag.
  if (isGroupAncestor(raw, draggedId, targetId)) return raw;

  const moved = source.group;

  function insertInto(list, parentId) {
    if (position === "inside") {
      if (parentId !== targetId) return list;
      return [...list, moved];
    }
    const at = list.findIndex((group) => _.isPlainObject(group) && group.id === targetId);
    if (at === -1) return list;
    const next = [...list];
    next.splice(position === "before" ? at : at + 1, 0, moved);
    return next;
  }

  function walk(groups, parentId) {
    const kept = [];
    for (const group of Array.isArray(groups) ? groups : []) {
      if (!_.isPlainObject(group)) {
        kept.push(group);
        continue;
      }
      if (group.id === draggedId) continue;
      const next = { ...group };
      next.groups = walk(group.groups, group.id);
      // A folder that ends up with no children keeps the key off rather than
      // carrying an empty array into the file.
      if (!next.groups.length) delete next.groups;
      kept.push(next);
    }
    return insertInto(kept, parentId);
  }

  // `inside` on a target that itself has no `groups` yet: walk creates the list
  // on the way past, because insertInto is called for every parent id.
  const groups = walk(raw.groups, null);
  return { ...raw, groups };
}

/** Append a new, empty group — at the root, or inside `parentId`. */
export function addGroup(raw, label, parentId = null) {
  if (!_.isPlainObject(raw)) return raw;

  const id = `group_${Date.now().toString(36)}`;
  const created = { id, label: String(label || "New Group").trim() || "New Group", fields: [] };

  if (!parentId) return { ...raw, groups: [...(raw.groups || []), created] };

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      if (group.id === parentId) return { ...group, groups: [...(group.groups || []), created] };
      if (Array.isArray(group.groups)) return { ...group, groups: walk(group.groups) };
      return group;
    });
  }
  return { ...raw, groups: walk(raw.groups) };
}

/** Rename one group. */
export function renameGroup(raw, id, label) {
  if (!_.isPlainObject(raw)) return raw;
  const trimmed = String(label || "").trim();
  if (!trimmed) return raw;

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      const next = group.id === id ? { ...group, label: trimmed } : { ...group };
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      return next;
    });
  }
  return { ...raw, groups: walk(raw.groups) };
}

/**
 * What a group holds, all the way down — what a delete would take with it.
 *
 * @returns {{fields: number, groups: number}}
 */
export function groupContents(raw, id) {
  const located = locateGroup(_.get(raw, "groups", []), id);
  if (!located) return { fields: 0, groups: 0 };

  let fields = 0;
  let groups = 0;
  (function walk(group) {
    fields += Array.isArray(group.fields) ? group.fields.length : 0;
    (Array.isArray(group.groups) ? group.groups : []).forEach((child) => {
      groups += 1;
      walk(child);
    });
  })(located.group);

  return { fields, groups };
}

/**
 * Remove a group.
 *
 * BY DEFAULT IT REFUSES TO TAKE FIELDS WITH IT: a drag-happy afternoon must not
 * silently drop rows the design was the only record of, so an emptied group is
 * the ordinary delete and children move up to where their parent was.
 *
 * `withContents` is the deliberate version — everything under the group goes
 * with it — and exists because emptying a scaffolded design one row at a time
 * is most of the work of cleaning one up. The caller is expected to have said
 * out loud what will be lost; nothing here asks.
 */
export function removeGroup(raw, id, { withContents = false } = {}) {
  if (!_.isPlainObject(raw)) return raw;

  const located = locateGroup(_.get(raw, "groups", []), id);
  if (!located) return raw;
  const holdsFields = Array.isArray(located.group.fields) && located.group.fields.length > 0;
  if (holdsFields && !withContents) return raw;

  const orphans = withContents
    ? []
    : Array.isArray(located.group.groups)
      ? located.group.groups
      : [];

  function walk(groups) {
    const kept = [];
    for (const group of Array.isArray(groups) ? groups : []) {
      if (!_.isPlainObject(group)) {
        kept.push(group);
        continue;
      }
      if (group.id === id) {
        kept.push(...orphans);
        continue;
      }
      const next = { ...group };
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      kept.push(next);
    }
    return kept;
  }
  return { ...raw, groups: walk(raw.groups) };
}

// ---------------------------------------------------------------------------
// Editing fields
//
// A field belongs to exactly one group, and that membership is the only thing
// the design decides about it — the value itself lives in `globalVariables` and
// is not touched by anything here. So moving a field is moving a descriptor
// between two arrays, and removing one removes a row from the panel, never a
// value from the snapshot.
// ---------------------------------------------------------------------------

/** The raw group holding this field path, the field, and its index — or null. */
function locateField(groups, path) {
  for (const group of Array.isArray(groups) ? groups : []) {
    if (!_.isPlainObject(group)) continue;
    const fields = Array.isArray(group.fields) ? group.fields : [];
    const index = fields.findIndex((field) => _.isPlainObject(field) && field.path === path);
    if (index !== -1) return { group, field: fields[index], index };
    const found = locateField(group.groups, path);
    if (found) return found;
  }
  return null;
}

/** Whether any group already declares this path. */
export function designHasFieldPath(raw, path) {
  return Boolean(locateField(_.get(raw, "groups", []), path));
}

/**
 * Move one field into another group.
 *
 * Appended at the end of the target's list rather than inserted at a position:
 * a group's field order is the reading order the author chose, and dropping a
 * field into the middle of it from a menu would be a guess. Reordering within a
 * group is a separate thing and is not offered yet.
 *
 * @param {object} raw
 * @param {string} path         the field's globalVariables path
 * @param {string} targetGroupId
 * @returns {object} a new design, or the original when the move is impossible
 */
export function moveField(raw, path, targetGroupId) {
  if (!_.isPlainObject(raw) || !path || !targetGroupId) return raw;

  const located = locateField(raw.groups, path);
  if (!located) return raw;
  if (located.group.id === targetGroupId) return raw;
  if (!locateGroup(_.get(raw, "groups", []), targetGroupId)) return raw;

  const moved = located.field;

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      const next = { ...group };

      if (Array.isArray(group.fields)) {
        next.fields = group.fields.filter(
          (field) => !(_.isPlainObject(field) && field.path === path)
        );
      }
      if (group.id === targetGroupId) {
        next.fields = [...(next.fields || []), moved];
      }
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      return next;
    });
  }

  return { ...raw, groups: walk(raw.groups) };
}

/**
 * Move a field one place up or down inside its own group.
 *
 * The counterpart to `moveField`, which moves a row to ANOTHER group and
 * appends it there. Within a group the order is the reading order the author
 * chose, so it is edited a step at a time rather than by dropping a row at a
 * guessed position.
 *
 * @param {object} raw
 * @param {string} path
 * @param {number} offset -1 for up, 1 for down
 * @returns {object} a new design, or the original when the row is already at
 *   that end of its group
 */
export function reorderField(raw, path, offset) {
  if (!_.isPlainObject(raw) || !path || (offset !== -1 && offset !== 1)) return raw;

  const located = locateField(_.get(raw, "groups", []), path);
  if (!located) return raw;

  const target = located.index + offset;
  const fields = Array.isArray(located.group.fields) ? located.group.fields : [];
  if (target < 0 || target >= fields.length) return raw;

  const reordered = [...fields];
  [reordered[located.index], reordered[target]] = [reordered[target], reordered[located.index]];

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      const next = { ...group };
      if (group.id === located.group.id) next.fields = reordered;
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      return next;
    });
  }

  return { ...raw, groups: walk(raw.groups) };
}

/**
 * Drop a field from the design.
 *
 * The value stays in the snapshot and stays editable from Custom Variables —
 * this only stops the Design tab claiming to describe it.
 */
export function removeField(raw, path) {
  if (!_.isPlainObject(raw) || !path) return raw;
  if (!locateField(raw.groups, path)) return raw;

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      const next = { ...group };
      if (Array.isArray(group.fields)) {
        next.fields = group.fields.filter(
          (field) => !(_.isPlainObject(field) && field.path === path)
        );
      }
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      return next;
    });
  }

  return { ...raw, groups: walk(raw.groups) };
}

/**
 * Write one field descriptor into a group.
 *
 * WHAT GETS WRITTEN IS WHAT WAS DECIDED. Keys the author left blank are left
 * out of the file rather than written as null: a design is hand-edited as often
 * as it is edited here, and a descriptor padded with nulls is harder to read
 * than one carrying only what someone chose. `normalizeField` fills the rest in
 * on load anyway.
 *
 * REFUSES A DUPLICATE PATH. Two rows writing to one path is not an error the
 * renderer reports — `designFieldsByPath` keeps the first and the second simply
 * never appears — so it has to be refused here or it looks like the add did
 * nothing.
 *
 * @param {object} raw
 * @param {string} groupId
 * @param {object} spec  path, type, and whichever of label/help/responsive/
 *                       control/min/max/step/options/localizable/readOnly apply
 * @returns {object} a new design, or the original when the field cannot be added
 */
/**
 * The type a spec asks for, in the two spellings both mean.
 *
 * @returns {{type: string, responsive: boolean}|null} null for a type no
 *   renderer knows, which is a refusal rather than a default.
 */
function readSpecType(spec) {
  let type = typeof spec.type === "string" ? spec.type.trim() : "";
  let responsive = spec.responsive === true;
  // Accept the sniffer's own spelling, so a type read off a value can be handed
  // straight back here.
  if (type.startsWith("responsive-")) {
    type = type.slice("responsive-".length);
    responsive = true;
  }
  type = LEGACY_TYPE_ALIASES[type] || type;
  if (!DESIGN_FIELD_TYPES.has(type)) return null;
  return { type, responsive };
}

/**
 * An asset descriptor.
 *
 * An asset row carries none of the value modifiers: there is no orientation
 * pair, no slider bounds, no enum and no translation for a picture.
 */
function buildAssetField(path, spec) {
  // A key typed without the prefix is what someone means; the file always
  // carries the prefixed form so the raw tree and the rendered tree agree.
  const assetPath = assetFieldPath(path);
  if (!assetKeyFromPath(assetPath)) return null;

  const field = { path: assetPath, type: "asset" };

  const label = typeof spec.label === "string" ? spec.label.trim() : "";
  if (label) field.label = label;
  const help = typeof spec.help === "string" ? spec.help.trim() : "";
  if (help) field.help = help;
  if (spec.readOnly === true) field.readOnly = true;

  return field;
}

/** A value descriptor — everything an asset row is not. */
function buildValueField(path, type, responsive, spec) {
  const field = { path, type };
  if (responsive) field.responsive = true;

  const label = typeof spec.label === "string" ? spec.label.trim() : "";
  if (label) field.label = label;

  const help = typeof spec.help === "string" ? spec.help.trim() : "";
  if (help) field.help = help;

  if (_.isFinite(spec.min)) field.min = spec.min;
  if (_.isFinite(spec.max)) field.max = spec.max;
  if (_.isFinite(spec.step)) field.step = spec.step;

  // Only meaningful with bounds — `normalizeField` demotes a bound-less slider
  // back to the scrub field, so writing it would promise a control the panel
  // will not render.
  if (spec.control === "slider" && _.isFinite(spec.min) && _.isFinite(spec.max)) {
    field.control = "slider";
  }

  const options = Array.isArray(spec.options)
    ? spec.options.filter((option) => _.isPlainObject(option) && option.value !== undefined)
    : [];
  if (options.length) {
    field.options = options.map((option) => ({
      value: option.value,
      label: String(option.label ?? option.value),
    }));
  }

  if (spec.localizable === true) field.localizable = true;
  if (spec.readOnly === true) field.readOnly = true;

  return field;
}

export function addField(raw, groupId, spec) {
  if (!_.isPlainObject(raw) || !_.isPlainObject(spec) || !groupId) return raw;

  const path = typeof spec.path === "string" ? spec.path.trim() : "";
  if (!path) return raw;

  const declared = readSpecType(spec);
  if (!declared) return raw;

  const field =
    declared.type === "asset"
      ? buildAssetField(path, spec)
      : buildValueField(path, declared.type, declared.responsive, spec);
  if (!field) return raw;

  if (!locateGroup(_.get(raw, "groups", []), groupId)) return raw;
  if (designHasFieldPath(raw, field.path)) return raw;

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      const next = { ...group };
      if (group.id === groupId) next.fields = [...(group.fields || []), field];
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      return next;
    });
  }

  return { ...raw, groups: walk(raw.groups) };
}

/**
 * The descriptor a path is described by, as it is written in the FILE, plus the
 * group holding it — what an edit form has to be seeded from.
 *
 * @returns {{field: object, groupId: string}|null}
 */
export function fieldAtPath(raw, path) {
  const located = locateField(_.get(raw, "groups", []), path);
  if (!located) return null;
  return { field: located.field, groupId: located.group.id };
}

/**
 * Rewrite one field descriptor in place.
 *
 * PATH IS IDENTITY here as everywhere else in this module, so the path is the
 * one thing an edit cannot change: the row keeps describing the value it
 * described, and the group keeps its reading order. Retargeting a row at
 * another value is removing it and adding the other one.
 *
 * Built from the spec rather than merged into the old field, for the reason
 * `addField` writes only what was decided: a type change leaves bounds and
 * enums behind that no longer mean anything, and a merge would keep them.
 *
 * @returns {object} a new design, or the original when the edit is impossible
 */
export function updateField(raw, path, spec) {
  if (!_.isPlainObject(raw) || !_.isPlainObject(spec) || !path) return raw;

  const located = locateField(_.get(raw, "groups", []), path);
  if (!located) return raw;

  const declared = readSpecType(spec);
  if (!declared) return raw;

  // An asset row and a value row are keyed differently — one names a slot in the
  // asset map, the other a globalVariables path — so one cannot become the other
  // without changing its path, which an edit does not do.
  const wasAsset = _.get(located.field, "type") === "asset";
  if (wasAsset !== (declared.type === "asset")) return raw;

  const field = wasAsset
    ? buildAssetField(path, spec)
    : buildValueField(path, declared.type, declared.responsive, spec);
  if (!field) return raw;

  function walk(groups) {
    return (Array.isArray(groups) ? groups : []).map((group) => {
      if (!_.isPlainObject(group)) return group;
      const next = { ...group };
      if (Array.isArray(group.fields)) {
        next.fields = group.fields.map((existing) =>
          _.isPlainObject(existing) && existing.path === path ? field : existing
        );
      }
      if (Array.isArray(group.groups)) next.groups = walk(group.groups);
      return next;
    });
  }

  return { ...raw, groups: walk(raw.groups) };
}

// ---------------------------------------------------------------------------
// Narrowing a design to one snapshot
//
// A design is a FILE, shared by every snapshot a flow generates from the same
// template — and a flow generates for more than one game. Pointed at a snapshot
// it was not written for, it would otherwise list that other game's components:
// rows for values this snapshot does not have, under groups it does not have
// either.
//
// So the panel shows what this snapshot actually holds. The design is never
// rewritten by this — it is a view, and the rows it leaves out come straight
// back when the design is opened on a snapshot that has them.
// ---------------------------------------------------------------------------

/** Whether this snapshot holds what a field describes. */
function snapshotHasField(field, variables, assets) {
  if (_.get(field, "type") === "asset") {
    const assetKey = assetKeyFromPath(_.get(field, "path"));
    return Boolean(assetKey) && _.has(assets || {}, [assetKey]);
  }
  return _.has(variables || {}, _.get(field, "path"));
}

/**
 * The design as this snapshot can show it: fields it does not hold are dropped,
 * and a group left with nothing — no fields of its own and no surviving group
 * inside it — goes with them.
 *
 * @param {object} design      a NORMALIZED design
 * @param {object} [snapshot]  { variables, assets } from the snapshot
 * @returns {object|null} a new design, or null when the design itself is null
 */
export function pruneDesignToSnapshot(design, { variables, assets } = {}) {
  if (!_.isPlainObject(design)) return design || null;

  function walk(groups) {
    return (Array.isArray(groups) ? groups : [])
      .map((group) => {
        const fields = (Array.isArray(group.fields) ? group.fields : []).filter((field) =>
          snapshotHasField(field, variables, assets)
        );
        const inner = walk(group.groups);
        return { ...group, fields, groups: inner };
      })
      .filter((group) => group.fields.length > 0 || group.groups.length > 0);
  }

  return { ...design, groups: walk(design.groups) };
}

/**
 * How much of a design this snapshot actually holds.
 *
 * The number behind "is this the right design for this snapshot?". A design
 * written for another game overlaps a little — every playable has a background
 * and a CTA — so the question is never "any match" but "enough of one".
 *
 * @returns {{total: number, present: number, ratio: number}} ratio is 1 for a
 *   design with no fields: nothing is missing from it.
 */
export function designCoverage(design, { variables, assets } = {}) {
  const fields = [];
  (function walk(groups) {
    (Array.isArray(groups) ? groups : []).forEach((group) => {
      (Array.isArray(group.fields) ? group.fields : []).forEach((field) => fields.push(field));
      walk(group.groups);
    });
  })(_.get(design, "groups", []));

  const present = fields.filter((field) => snapshotHasField(field, variables, assets)).length;
  return { total: fields.length, present, ratio: fields.length ? present / fields.length : 1 };
}

/**
 * Add rows for what this snapshot has and the design does not describe.
 *
 * The other half of pointing a design at a snapshot: pruning hides what does
 * not belong, this brings in what is missing. The rows come from the same
 * sniffer the starter generator uses, so a value scaffolded here and one
 * scaffolded there are described the same way.
 *
 * Groups are appended, never merged into the existing ones: which group a
 * setting belongs in is an authoring decision, and dropping a scaffolded field
 * into a hand-made group would be guessing at it. Whole groups can be renamed,
 * moved or emptied from the panel afterwards.
 *
 * @returns {object} a new design, or the original when nothing was missing
 */
export function mergeSnapshotSettings(raw, variables, options = {}) {
  if (!_.isPlainObject(raw)) return raw;

  const scaffolded = scaffoldDesign(variables, {
    id: raw.id,
    hiddenPaths: options.hiddenPaths,
  });

  const takenIds = new Set(walkGroups(normalizeDesign(raw)).map((entry) => entry.group.id));

  function keepMissing(groups) {
    return (Array.isArray(groups) ? groups : [])
      .map((group) => {
        const fields = (Array.isArray(group.fields) ? group.fields : []).filter(
          (field) => !designHasFieldPath(raw, field.path)
        );
        const inner = keepMissing(group.groups);
        if (!fields.length && !inner.length) return null;

        // A scaffolded group is keyed by its dotted path, which the design may
        // already use for a group of its own.
        let id = group.id;
        while (takenIds.has(id)) id = `${id}-new`;
        takenIds.add(id);

        return { ...group, id, fields, groups: inner };
      })
      .filter(Boolean);
  }

  const added = keepMissing(scaffolded.groups);
  if (!added.length) return raw;

  return { ...raw, groups: [...(Array.isArray(raw.groups) ? raw.groups : []), ...added] };
}

// ---------------------------------------------------------------------------
// Allowed values the engine already knows
//
// A component's `configSchema` is what the version settings panel reads to draw
// a dropdown instead of a text box. The same enums describe the values a
// snapshot keeps in `globalVariables` — an animation type is the same short
// list whichever side of the bridge asks — so a design row can be a dropdown
// without anyone typing the list into the design by hand.
//
// NOTHING IS INVENTED HERE. A key the engine declares no options for stays the
// control its type asks for, and a design that lists its own options keeps
// them: what the author wrote outranks what was discovered.
// ---------------------------------------------------------------------------

/** An engine option list in the shape the dropdown wants, or null. */
function normalizeSchemaOptions(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const options = raw
    .map((option) => {
      if (_.isPlainObject(option)) {
        if (option.value === undefined) return null;
        return { value: option.value, label: String(option.label ?? option.value) };
      }
      if (typeof option === "string" || typeof option === "number") {
        return { value: option, label: String(option) };
      }
      return null;
    })
    .filter(Boolean);

  return options.length ? options : null;
}

/**
 * Every config key the engine declares allowed values for, keyed by the key
 * itself — `animationType`, not `logo.animationType`, because the design names
 * the variable and the schema names the component's field.
 *
 * A key two components declare DIFFERENTLY is dropped rather than guessed at:
 * showing one component's list on another component's value would offer values
 * the engine will not accept.
 *
 * @param {Array} components `playengine:get-dependencies` data
 * @returns {Map<string, Array<{value: *, label: string}>>}
 */
export function schemaOptionsByKey(components) {
  const byKey = new Map();
  const conflicting = new Set();

  (Array.isArray(components) ? components : []).forEach((component) => {
    const schema = _.get(component, "configSchema");
    if (!_.isPlainObject(schema)) return;

    Object.keys(schema).forEach((key) => {
      const options = normalizeSchemaOptions(_.get(schema, [key, "options"]));
      if (!options) return;

      const known = byKey.get(key);
      if (!known) {
        byKey.set(key, options);
        return;
      }
      if (!_.isEqual(known, options)) conflicting.add(key);
    });
  });

  conflicting.forEach((key) => byKey.delete(key));
  return byKey;
}

/**
 * The engine's list for one design path, or null.
 *
 * The whole path first — a design that names the config key outright is the
 * unambiguous case — then its last segment, which is how a generated snapshot
 * spells the same field under the component it belongs to (`logo.animationType`).
 */
export function schemaOptionsForPath(byKey, path) {
  if (!(byKey instanceof Map) || byKey.size === 0 || typeof path !== "string") return null;

  const exact = byKey.get(path);
  if (exact) return exact;

  const segments = path.split(".");
  return segments.length > 1 ? byKey.get(segments[segments.length - 1]) || null : null;
}

// ---------------------------------------------------------------------------
// Scaffolding
// ---------------------------------------------------------------------------

/**
 * Every path under `value` a control could be drawn for, depth-first.
 *
 * An object is a container, not a field — descend into it, unless we have run
 * out of depth, in which case one `object` row beats nothing.
 *
 * @returns {Array<{path: string, type: string}>}
 */
function collectSniffable(value, dottedPath, depth, hidden, maxDepth) {
  if (hidden.has(dottedPath)) return [];

  const type = detectFieldType(value, dottedPath);

  if (type === "object" && depth < maxDepth) {
    return Object.keys(value).flatMap((key) =>
      collectSniffable(
        value[key],
        dottedPath ? `${dottedPath}.${key}` : key,
        depth + 1,
        hidden,
        maxDepth
      )
    );
  }
  if (!type) return [];

  return [{ path: dottedPath, type }];
}

/**
 * The paths in a snapshot that a design could describe, and what each one looks
 * like.
 *
 * The same rule the scaffold generator applies, exported so the "add a setting"
 * form can offer the same list. One definition, because a path the generator
 * would have picked up and the form will not offer — or the other way round —
 * is a difference nobody can explain from the UI.
 *
 * @param {object} variables the snapshot's globalVariables
 * @param {object} [options]
 * @param {Set<string>|string[]} [options.hiddenPaths]
 * @param {number} [options.maxDepth]
 */
export function sniffableFieldPaths(variables, options = {}) {
  const hidden =
    options.hiddenPaths instanceof Set
      ? options.hiddenPaths
      : new Set(options.hiddenPaths || []);
  const maxDepth = _.isFinite(options.maxDepth) ? options.maxDepth : SCAFFOLD_MAX_DEPTH;

  return Object.keys(variables || {}).flatMap((key) =>
    collectSniffable(variables[key], key, 1, hidden, maxDepth)
  );
}

/**
 * Build a starter design from the values a snapshot actually holds.
 *
 * The point is to get an author to the interesting part. Writing 31 top-level
 * groups by hand — which is what wordmaster needs — is the kind of chore that
 * stops a feature being used, while deleting the groups that turned out to be
 * runtime state (`score`, `taps`, `state`) takes seconds.
 *
 * So this is deliberately over-inclusive: everything sniffable becomes a field,
 * and pruning is the author's job. It never guesses `options`, `min`/`max` or
 * `localizable` — those are decisions, not observations, and a wrong guess is
 * worse than a blank.
 *
 * @param {object} variables the snapshot's globalVariables
 * @param {object} [options]
 * @param {Set<string>|string[]} [options.hiddenPaths] paths to leave out, e.g.
 *   an existing `inspectorConfig.globalVariables.hidden`
 * @param {string} [options.id] the design id, used as its own title fallback
 */
export function scaffoldDesign(variables, options = {}) {
  const hidden =
    options.hiddenPaths instanceof Set
      ? options.hiddenPaths
      : new Set(options.hiddenPaths || []);
  const id = options.id || null;

  /** Fields for everything sniffable under `value`, depth-first. */
  const collect = (value, dottedPath, depth) =>
    collectSniffable(value, dottedPath, depth, hidden, SCAFFOLD_MAX_DEPTH);

  /** `{path, type}` rows → design fields, labelled relative to their group. */
  function toFields(rows, groupPath) {
    return rows.map((row) => ({
      path: row.path,
      type: row.type,
      label: labelForPath(row.path, groupPath),
    }));
  }

  /**
   * One group for a container, nesting it when it is too big to scan.
   *
   * Under the threshold the whole container is one section. Over it, the
   * container keeps the values that sit directly on it and each sub-container
   * becomes a CHILD group — real nesting, so the tree can be collapsed and the
   * labels stay short.
   *
   * @returns {object|null} null when nothing under it is sniffable
   */
  function buildGroup(value, dottedPath, label, depth) {
    const rows = collect(value, dottedPath, depth);
    if (!rows.length) return null;

    const subContainers = Object.keys(value).filter(
      (key) =>
        !hidden.has(`${dottedPath}.${key}`) &&
        detectFieldType(value[key], `${dottedPath}.${key}`) === "object"
    );

    const tooBig = rows.length > SCAFFOLD_MAX_FIELDS_PER_GROUP;
    if (!tooBig || !subContainers.length || depth >= SCAFFOLD_MAX_DEPTH) {
      return { id: dottedPath, label, path: dottedPath, fields: toFields(rows, dottedPath) };
    }

    // Own values only — the children carry the rest.
    const ownRows = rows.filter((row) => row.path.split(".").length === depth + 1);
    const children = subContainers
      .map((key) =>
        buildGroup(value[key], `${dottedPath}.${key}`, humanizeKey(key), depth + 1)
      )
      .filter(Boolean);

    const group = { id: dottedPath, label, path: dottedPath, fields: toFields(ownRows, dottedPath) };
    if (children.length) group.groups = children;
    return group;
  }

  const groups = [];
  const loose = [];

  for (const key of Object.keys(variables || {})) {
    if (hidden.has(key)) continue;

    const value = variables[key];
    const type = detectFieldType(value, key);

    // Only a real container earns a group of its own. A top-level colour or
    // counter would be a group with one row in it, so those collect into
    // "General" at the end.
    if (type === "object") {
      const group = buildGroup(value, key, humanizeKey(key), 1);
      if (group) groups.push(group);
      continue;
    }

    if (type) loose.push({ path: key, type, label: humanizeKey(key) });
  }

  if (loose.length) {
    groups.push({ id: "general", label: "General", fields: loose });
  }

  return {
    formatVersion: DESIGN_FORMAT_VERSION,
    ...(id ? { id } : {}),
    title: id ? humanizeKey(id) : "Untitled Design",
    target: "globalVariables",
    groups,
  };
}
